import { createApp } from 'vue'
import './style.css'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import { createLogger } from './utils/logger'
import { initSentry } from './utils/sentry'
import * as Sentry from '@sentry/vue'
import { vPermission } from './directives/permission'

const logger = createLogger('PWA')

async function startApp() {
  const app = createApp(App)

  // Install plugins in order:
  // 1. Vuetify - UI framework
  app.use(vuetify)

  // 3. Register global directives
  app.directive('permission', vPermission)

  // 4. Router
  app.use(router)

  // Wait for router to be ready before mounting to ensure all navigation guards complete
  await router.isReady()

  // 4. Initialize Sentry error tracking (after router is ready, before mounting)
  initSentry(app, router)

  // 5. Configure global error handlers (after Sentry init, before mounting)

  // Vue Error Handler - Captures all Vue component errors
  app.config.errorHandler = (err, instance, info) => {
    // Log to console in development
    if (import.meta.env.DEV) {
      logger.error('Vue Error:', err)
      logger.error('Component:', instance?.$options.name || 'Unknown')
      logger.error('Info:', info)
    }

    // Capture in Sentry with rich context
    Sentry.withScope(scope => {
      // Set error type tag
      scope.setTag('error_type', 'vue')
      scope.setTag('error_info', info) // lifecycle hook name

      // Add component context
      if (instance) {
        scope.setContext('vue_component', {
          name: instance.$options.name || 'Anonymous',
          props: instance.$props,
          propsData: instance.$options.propsData,
        })

        // Add breadcrumb
        scope.addBreadcrumb({
          category: 'vue',
          message: `Error in component: ${instance.$options.name || 'Unknown'}`,
          level: 'error',
          data: {
            lifecycleHook: info,
            componentName: instance.$options.name,
          },
        })
      }

      // Capture exception
      Sentry.captureException(err)
    })

    // Don't prevent Vue's default error handling
    // (allows errors to still show in dev tools)
  }

  // Window Error Handler - Captures all unhandled JavaScript errors
  window.addEventListener('error', event => {
    // Prevent duplicate capture if Sentry already handled it
    if (event.error && '__sentryCapture__' in event.error) {
      return
    }

    // Log in development
    if (import.meta.env.DEV) {
      logger.error('Window Error:', event.error || event.message)
    }

    // Capture in Sentry
    Sentry.withScope(scope => {
      scope.setTag('error_type', 'window')

      // Add error details
      scope.setContext('error_event', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })

      // Add breadcrumb
      scope.addBreadcrumb({
        category: 'error',
        message: `Window error: ${event.message}`,
        level: 'error',
        data: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      })

      // Capture exception (prefer Error object if available)
      if (event.error) {
        // Mark to prevent duplicates
        Object.defineProperty(event.error, '__sentryCapture__', {
          value: true,
          writable: false,
          enumerable: false,
        })
        Sentry.captureException(event.error)
      } else {
        Sentry.captureMessage(event.message, 'error')
      }
    })

    // Allow default error handling
    return false
  })

  // Unhandled Promise Rejection Handler
  window.addEventListener('unhandledrejection', event => {
    // Log in development
    if (import.meta.env.DEV) {
      logger.error('Unhandled Promise Rejection:', event.reason)
    }

    // Capture in Sentry
    Sentry.withScope(scope => {
      scope.setTag('error_type', 'unhandled_promise')

      // Add promise context
      scope.setContext('promise_rejection', {
        reason: event.reason,
        promise: event.promise,
      })

      // Add breadcrumb
      scope.addBreadcrumb({
        category: 'promise',
        message: 'Unhandled promise rejection',
        level: 'error',
        data: {
          reason:
            event.reason instanceof Error
              ? event.reason.message
              : String(event.reason),
        },
      })

      // Capture exception
      if (event.reason instanceof Error) {
        Sentry.captureException(event.reason)
      } else {
        Sentry.captureMessage(
          `Unhandled promise rejection: ${String(event.reason)}`,
          'error'
        )
      }
    })

    // Prevent default handling (we've logged it)
    event.preventDefault()
  })

  // 6. Expose error testing utilities in development
  if (import.meta.env.DEV) {
    const { exposeToWindow } = await import('./utils/errorTesting')
    exposeToWindow()
  }

  // 7. Mount the app - now all plugins are installed and router is ready
  app.mount('#app')
}

// Start the app
startApp().catch(error => {
  logger.error('Failed to start app:', error)
  Sentry.captureException(error, {
    tags: { error_type: 'app_startup' },
  })
})

// Register service worker in staging and production builds
// Service worker is disabled in:
// - Development (npm run dev): Interferes with hot-reload and causes stale module caching
// - E2E tests (playwright): Blocked via serviceWorkers: 'block' in playwright.config.ts
// Service worker runs in staging and production deployments
if (
  import.meta.env.MODE === 'staging' ||
  import.meta.env.MODE === 'production'
) {
  // Dynamic import to avoid bundling in development
  const { registerSW } = await import('virtual:pwa-register')
  const updateSW = registerSW({
    onNeedRefresh() {
      logger.info('New version available, updating automatically')
      // Automatically update without user prompt
      updateSW(true)
    },
    onOfflineReady() {
      logger.info('App is ready to work offline')
    },
    onRegisteredSW(swUrl, registration) {
      logger.info('Service worker registered:', swUrl)
      // Check for updates every 60 seconds when app is active
      if (registration) {
        setInterval(() => {
          logger.debug('Checking for updates...')
          registration.update()
        }, 60000)
      }
    },
    immediate: true,
  })
} else {
  logger.info('Service worker disabled in development mode')
}
